/* eslint-disable @typescript-eslint/triple-slash-reference */
/// <reference path="./IntlTelInputWrapper.d.ts" />
import React, { useCallback, useEffect, useRef } from 'react';
import type { IntlTelInputRef } from 'intl-tel-input/reactWithUtils';
import type IntlTelInputType from 'intl-tel-input/reactWithUtils';
import { loadable } from '@shared/core/loadableFromNamedExport';
import 'intl-tel-input/build/css/intlTelInput.css';

export type { IntlTelInputRef };

type IntlTelInputProps = React.ComponentPropsWithoutRef<typeof IntlTelInputType>;

type Props = Readonly<
  Omit<IntlTelInputProps, 'onChangeNumber' | 'onChangeValidity' | 'initialValue'> & {
    readonly initialValue?: string | null;
    readonly onChangeNumber?: (number: string) => void;
    readonly onChangeValidity?: (valid: boolean) => void;
    // Change this value to reset the input (cannot use React key due to intl-tel-input DOM manipulation)
    readonly resetKey?: React.Key | null;
    /**
     * Focus the number once intl-tel-input is ready — named after `InputV2`'s prop of the same job.
     * Not the input's own `autoFocus`: the library moves the input into its wrapper when it
     * initialises, which drops whatever focus the input had.
     */
    readonly enableFocusOnMount?: boolean;
  }
>;

const IntlTelInputLoadable = loadable(
  () =>
    import(
      /* webpackChunkName: "static/webpack/vendor/intl-tel-input/reactWithUtils" */
      'intl-tel-input/reactWithUtils'
    ),
  {
    ssr: false,
  },
);

/**
 * How many frames `enableFocusOnMount` waits for the library — about five seconds at 60fps. A chunk
 * that failed to load never initialises, and the wait must not outlive it.
 */
export const FOCUS_ON_MOUNT_FRAME_LIMIT = 300;

export const IntlTelInputWrapper: React.FC<Props> = ({ initialValue, onChangeNumber, onChangeValidity, resetKey, enableFocusOnMount, ...passThroughProps }) => {
  const inputRef = useRef<IntlTelInputRef>(null);
  const initialValueRef = useRef<typeof initialValue>(initialValue);

  // Track last values to prevent infinite loops from IntlTelInput firing on every render
  const lastPhoneNumberRef = useRef<string | undefined>(undefined);
  const lastValidityRef = useRef<boolean | undefined>(undefined);
  const onChangeNumberRef = useRef(onChangeNumber);
  const onChangeValidityRef = useRef(onChangeValidity);

  // Keep refs fresh without re-creating callbacks
  onChangeNumberRef.current = onChangeNumber;
  onChangeValidityRef.current = onChangeValidity;
  initialValueRef.current = initialValue;

  // Reset input when resetKey changes (intl-tel-input manipulates DOM, so we must reset imperatively)
  useEffect(() => {
    const resetValue = initialValueRef.current ?? '';
    // Set lastPhoneNumberRef to the reset value BEFORE calling setNumber
    // This prevents the onChangeNumber callback from triggering formik updates
    // which could interfere with other instances' reset effects
    lastPhoneNumberRef.current = resetValue;
    lastValidityRef.current = undefined;
    inputRef.current?.getInstance()?.setNumber(resetValue);
  }, [resetKey]);

  // Read once, like the field's other init options: focusing is something that happens on arrival.
  const focusOnMountRef = useRef(enableFocusOnMount);

  // The input only exists once the lazy chunk has loaded, and is only in its final place once the
  // library has initialised on it, so this waits a frame at a time for both before focusing — up to
  // `FOCUS_ON_MOUNT_FRAME_LIMIT` frames, after which focus is simply left where it is.
  useEffect(() => {
    if (!focusOnMountRef.current) return undefined;
    let frame: number;
    let framesLeft = FOCUS_ON_MOUNT_FRAME_LIMIT;
    const focusWhenReady = () => {
      const field = inputRef.current;
      if (field?.getInstance()) {
        field.getInput()?.focus();
        return;
      }
      framesLeft -= 1;
      if (framesLeft > 0) frame = requestAnimationFrame(focusWhenReady);
    };
    frame = requestAnimationFrame(focusWhenReady);
    return () => cancelAnimationFrame(frame);
  }, []);

  const handleChangeNumber = useCallback((number: string) => {
    if (lastPhoneNumberRef.current !== number) {
      lastPhoneNumberRef.current = number;
      onChangeNumberRef.current?.(number);
    }
  }, []);

  const handleChangeValidity = useCallback((valid: boolean) => {
    if (lastValidityRef.current !== valid) {
      lastValidityRef.current = valid;
      onChangeValidityRef.current?.(valid);
    }
  }, []);

  return (
    <IntlTelInputLoadable
      ref={inputRef}
      initialValue={initialValue ?? undefined}
      onChangeNumber={handleChangeNumber}
      onChangeValidity={handleChangeValidity}
      {...passThroughProps}
    />
  );
};
